import { Menu } from 'antd5';
import { Link } from 'react-router-dom';
import styled, { type DefaultTheme, css } from 'styled-components';

import { Sider } from 'components/ui';
import { Theme } from 'utils/color/horizonTheme';
import { FlexContainer } from 'utils/layouts/containers';

const SIDEBAR_TRIGGER_HEIGHT = '48px';
const SIDEBAR_LOGO_WIDTH = '8.5rem';
const SIDEBAR_LOGO_HEIGHT = '1.5rem';

export const Logo = styled(FlexContainer)`
  background: ${({ theme }) => theme.sidebarBg};
  text-align: center;
  overflow: hidden;
  height: 65px;
  justify-content: center;
  border-bottom: 1px solid ${({ theme }) => theme.sidebarBorder};
`;

export const Image = styled.img<{ $collapsed?: boolean; $invertForNavyRail?: boolean }>`
  width: ${({ $collapsed }) => ($collapsed ? '48px' : '100%')};
  max-height: ${({ $collapsed }) => ($collapsed ? '48px' : '52px')};
  object-fit: contain;
  padding: ${({ $collapsed }) => ($collapsed ? '0.5em' : '0.5rem 0.75rem')};
  filter: ${({ $invertForNavyRail }) => ($invertForNavyRail ? 'brightness(0) invert(1)' : 'none')};
`;

// !important for overriding antd
// Trevor: Not sure where the "margin-right: 10px" is coming from on the icon property
// for the Menu, but it causes a larger space between the icon and title than what we want.
// When inspecting the antd menu on their examples on the antd site. It does not have
// the "margin-right: 10px" property.
export const StyledMenu = styled(Menu)`
  width: 100%;
  .ant-menu-item {
    font-size: 16px !important;
  }
  .ant-menu-item .ant-menu-item-icon {
    margin-right: 0px;
  }

  ${({ theme }) =>
    theme.isHorizonGridVisualRefresh
      ? css`
          .ant-menu-item-disabled,
          .ant-menu-item-disabled > span,
          .ant-menu-item-disabled > span > a {
            color: ${theme.sidebarTextMuted} !important;
          }
        `
      : ''}

  .ant-menu-item-disabled {
    display: flex;

    svg {
      stroke: ${({ theme }) =>
        theme.isHorizonGridVisualRefresh ? theme.sidebarTextMuted : theme.lightShadow} !important;
      ${({ theme }) =>
        theme.isHorizonGridVisualRefresh ? `color: ${theme.sidebarTextMuted} !important;` : ''}
    }

    .ant-menu-title-content {
      display: inline-flex;
      flex-grow: 1;

      span {
        width: 100%;
      }
    }
  }
`;

export const SidebarFooter = styled.div<{ $collapsed?: boolean }>`
  padding: ${({ $collapsed }) => ($collapsed ? '0.75rem 0.5rem 0.25rem' : '0.75rem 1rem 0.25rem')};
  border-top: 1px solid ${({ theme }) => theme.sidebarBorder};
  overflow: hidden;
`;

export const SidebarMeta = styled.div`
  position: absolute;
  right: 0;
  bottom: ${SIDEBAR_TRIGGER_HEIGHT};
  left: 0;
  display: flex;
  flex-direction: column;
`;

export const SidebarLogoLink = styled(Link)<{ $collapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  padding: 0 1rem 0.5rem;
  overflow: hidden;
`;

export const SidebarLogoImage = styled.img<{ $collapsed?: boolean; $invertForNavyRail?: boolean }>`
  display: block;
  width: ${({ $collapsed }) => ($collapsed ? SIDEBAR_LOGO_HEIGHT : SIDEBAR_LOGO_WIDTH)};
  height: ${SIDEBAR_LOGO_HEIGHT};
  filter: ${({ $invertForNavyRail }) => ($invertForNavyRail ? 'brightness(0) invert(1)' : 'none')};
`;

export const VersionLabel = styled.span<{ $collapsed?: boolean }>`
  display: block;
  padding: ${({ $collapsed }) => ($collapsed ? '0 0.25rem 0.5rem' : '0 1rem 0.5rem')};
  /* Match the sidebar background so the version blends in but stays selectable:
   * hidden to most users, readable when highlighted (with cursor/user-select below). */
  color: ${({ theme }) => theme.sidebarBg};
  font-size: 0.75rem;
  line-height: 1.3;
  text-align: center;
  user-select: text;
  cursor: text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
`;

export const PrivacyPolicyLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  white-space: nowrap;
  color: ${({ theme }) => theme.sidebarText};

  &:hover {
    color: ${({ theme }) => theme.teal};
  }
`;

/**
 * antd's Sider component has a 'theme' prop, so we need to rename
 * the styled-components theme prop here
 */
export const StyledSider = styled(Sider)<{ $styledComponentsTheme: DefaultTheme }>`
  background: ${({ $styledComponentsTheme }) => $styledComponentsTheme.sidebarBg} !important;
  border-right: 1px solid ${({ $styledComponentsTheme }) => $styledComponentsTheme.sidebarBorder};
  display: flex;
  flex-direction: column;
  position: relative;

  .ant-layout-sider-children {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 100%;
  }

  .ant-menu,
  .ant-menu-dark {
    background: ${({ $styledComponentsTheme }) => $styledComponentsTheme.sidebarBg} !important;

    .ant-menu-item {
      color: ${({ $styledComponentsTheme }) => $styledComponentsTheme.sidebarText} !important;

      > span > a {
        color: ${({ $styledComponentsTheme }) => $styledComponentsTheme.sidebarText} !important;
      }

      ${({ $styledComponentsTheme }) =>
        $styledComponentsTheme.isHorizonGridVisualRefresh
          ? css`
              svg {
                color: ${$styledComponentsTheme.teal} !important;
              }

              &.ant-menu-item-selected,
              &.ant-menu-item-selected > span > a {
                color: ${$styledComponentsTheme.mapControlsTextColor} !important;
                background-color: ${$styledComponentsTheme.sidebarActiveBg} !important;
              }

              &.ant-menu-item-selected {
                box-shadow: inset 3px 0 0 ${$styledComponentsTheme.teal};
              }

              &.ant-menu-item-selected svg {
                color: ${$styledComponentsTheme.mapControlsTextColor} !important;
              }

              &:hover {
                background-color: ${$styledComponentsTheme.sidebarActiveBg} !important;
              }
            `
          : css`
              ${$styledComponentsTheme.themeName === Theme.Dark
                ? css`
                    &.ant-menu-item-selected {
                      background-color: ${$styledComponentsTheme.teal};
                    }
                  `
                : ''}

              ${$styledComponentsTheme.themeName === Theme.Light
                ? css`
                    &.ant-menu-item-selected,
                    &.ant-menu-item-selected > span > a {
                      color: ${$styledComponentsTheme.teal} !important;
                    }
                  `
                : ''}

              &:hover {
                background-color: ${$styledComponentsTheme.themeName === Theme.Dark
                  ? $styledComponentsTheme.activeMenuItemBackgroundColor
                  : $styledComponentsTheme.borderAccentColor} !important;
              }
            `}
    }
  }

  .ant-layout-sider-trigger {
    background: ${({ $styledComponentsTheme }) =>
      $styledComponentsTheme.isHorizonGridVisualRefresh
        ? $styledComponentsTheme.sidebarBorder
        : $styledComponentsTheme.themeName === Theme.Dark
          ? $styledComponentsTheme.primaryBackgroundColor
          : $styledComponentsTheme.gray} !important;
    ${({ $styledComponentsTheme }) =>
      $styledComponentsTheme.isHorizonGridVisualRefresh
        ? ''
        : `border-right: 1px solid ${$styledComponentsTheme.borderColor};`}

    svg {
      color: ${({ $styledComponentsTheme }) =>
        $styledComponentsTheme.isHorizonGridVisualRefresh
          ? $styledComponentsTheme.sidebarText
          : $styledComponentsTheme.descriptionColor} !important;
    }
  }
`;
